import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  LinearProgress,
  Box,
  alpha,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Clock, AlertTriangle } from "lucide-react";

interface CountdownWidgetProps {
  move: { pickupDate: string };
}

const CountDown = ({ move }: CountdownWidgetProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [daysRemaining, setDaysRemaining] = useState<number>(0);
  const [progress, setProgress] = useState<number>(0);
  const [movePhase, setMovePhase] = useState<
    "planning" | "packing" | "final" | "imminent"
  >("planning");

  useEffect(() => {
    const moveDate = new Date(move.pickupDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    moveDate.setHours(0, 0, 0, 0);

    const timeDiff = moveDate.getTime() - today.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
    const displayDays = Math.max(0, daysDiff);
    setDaysRemaining(displayDays);
    if (daysDiff > 0) {
      if (daysDiff <= 2) setMovePhase("imminent");
      else if (daysDiff <= 7) setMovePhase("final");
      else if (daysDiff <= 14) setMovePhase("packing");
      else setMovePhase("planning");
    } else {
      setMovePhase("imminent");
    }

    const preparationPeriod = 30;
    const daysElapsed = preparationPeriod - daysDiff;
    const calculatedProgress = Math.min(
      100,
      Math.max(0, (daysElapsed / preparationPeriod) * 100)
    );
    setProgress(calculatedProgress);
  }, [move.pickupDate]);

  const getMoveTip = () => {
    switch (movePhase) {
      case "planning":
        return "Start decluttering and make an inventory of your items.";
      case "packing":
        return "Begin packing non-essential items and schedule utility transfers.";
      case "final":
        return "Finish packing, confirm move details, and prepare a day-of kit.";
      case "imminent":
        return daysRemaining > 0 
          ? "Have important documents and valuables ready to transport personally."
          : "Your move date has passed.";
      default:
        return "";
    }
  };

  const extractDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toISOString().split("T")[0]; 
  };

  const getHeaderStyle = () => {
    return movePhase === "imminent"
      ? theme.palette.warning.main
      : theme.palette.primary.main;
  };

  const getCardStyle = () => {
    return movePhase === "imminent"
      ? alpha(theme.palette.warning.main, 0.1)
      : alpha(theme.palette.primary.main, 0.1);
  };

  const getTextColor = () => {
    return movePhase === "imminent"
      ? theme.palette.warning.dark
      : theme.palette.primary.dark;
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Card
        sx={{
          bgcolor: getCardStyle(),
          borderRadius: 2,
          p: isMobile ? 1 : 2,
          border: `1px solid ${theme.palette.primary.main}`,
        }}
      >
        <Typography 
          ml={isMobile ? 1 : 2} 
          fontSize={isMobile ? 14 : 16} 
          fontWeight={600}
        >
          {`Your Move Date: ` + extractDate(move.pickupDate)}
        </Typography>

        <CardHeader
          sx={{ p: isMobile ? 1 : 2 }}
          title={
            <Typography
              variant={isMobile ? "subtitle1" : "h6"}
              sx={{
                color: getHeaderStyle(),
                display: "flex",
                alignItems: "center",
              }}
            >
              <Box sx={{ mr: 1 }}>
                {movePhase === "imminent" ? <AlertTriangle size={isMobile ? 18 : 24} /> : <Clock size={isMobile ? 18 : 24} />}
              </Box>
              {movePhase === "imminent" && daysRemaining > 0
                ? "Move Day Is Almost Here!"
                : "Move Day Countdown"}
            </Typography>
          }
        />
        <CardContent sx={{ p: isMobile ? 1 : 2 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={isMobile ? 1 : 2}
          >
            <Typography 
              variant={isMobile ? "h5" : "h4"} 
              sx={{ color: getTextColor() }}
            >
              {daysRemaining} {daysRemaining === 1 ? "Day" : "Days"}
            </Typography>
            <Typography 
              variant={isMobile ? "caption" : "body2"} 
              sx={{ color: getTextColor() }}
            >
              Until Your Move
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ 
              mb: isMobile ? 1 : 2,
              height: isMobile ? 6 : 8 
            }}
          />
          <Typography 
            variant={isMobile ? "caption" : "body2"} 
            sx={{ color: getTextColor() }}
          >
            Preparation
          </Typography>
          <Typography 
            variant={isMobile ? "caption" : "body2"} 
            sx={{ 
              color: getTextColor(), 
              mt: isMobile ? 0.5 : 1 
            }}
          >
            Pro Tip: {getMoveTip()}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}

export default CountDown;